/**
 * Extract a JPEG thumbnail frame from a video file (client-side, before
 * encryption). Seeks slightly into the video to avoid black intro frames.
 * Returns null on any failure (unsupported codec, timeout, …).
 */
export function extractVideoFrame(file: File, maxDim = 640): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    let done = false;
    const finish = (result: Blob | null) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
      resolve(result);
    };
    const timeout = setTimeout(() => finish(null), 20000);

    video.addEventListener('loadeddata', () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 1;
      video.currentTime = Math.min(1, Math.max(0.1, duration * 0.1));
    });
    video.addEventListener('seeked', () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return finish(null);
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => finish(b), 'image/jpeg', 0.82);
      } catch {
        finish(null);
      }
    });
    video.addEventListener('error', () => finish(null));

    video.src = url;
  });
}
